import { DatePipe, UpperCasePipe } from '@angular/common'
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	Signal,
	signal,
	WritableSignal,
	inject,
} from '@angular/core'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { TuiIcon } from '@taiga-ui/core'
import { finalize, Subject, takeUntil } from 'rxjs'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { PositionOptionInterface } from '@modules/options-module/positions/interfaces/position-option.interface'
import { OptionTypeEnum } from '@modules/options-module/general/enums/option-type.enum'
import { ReactiveFormsModule } from '@angular/forms'
import { TuiInputModule, TuiTextfieldControllerModule } from '@taiga-ui/legacy'
import { AlpacaAccountInterface } from '@modules/alpaca-module/alpaca-account/interfaces/alpaca-account.interface'
import { HttpErrorResponse } from '@angular/common/http'
import { OptionSharesPerContractConstant } from '@modules/options-module/general/constants/option-shares-per-contract.constant'
import { PositionsOptionsService } from '@modules/options-module/positions/services/positions-options.service'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { AlertService } from '@shared/services/alert.service'
import { AlpacaAccountHelperService } from '@modules/alpaca-module/alpaca-account/services/alpaca-account-helper.service'
import { ButtonComponent } from '@shared/components/ui-kit/button/button.component'

@UseLocales({ ru, en })
@Component({
	selector: 'app-position-option-execution',
	templateUrl: './position-option-execution.component.html',
	styleUrl: './position-option-execution.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TuiIcon,
		TranslatePipe,
		HighlightDirective,
		UpperCasePipe,
		DatePipe,
		ReactiveFormsModule,
		TuiInputModule,
		TuiTextfieldControllerModule,
		PricePipe,
		QuantityPipe,
		ButtonComponent,
	],
})
export class PositionOptionExecutionComponent implements OnInit, OnDestroy {
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum
	protected readonly optionTypeEnum: typeof OptionTypeEnum = OptionTypeEnum
	protected readonly optionSharesPerContractConstant: number = OptionSharesPerContractConstant

	public position: InputSignal<PositionOptionInterface | null> = input<PositionOptionInterface | null>(null)
	public closeDropdown: OutputEmitterRef<void> = output<void>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _positionsOptionsService: PositionsOptionsService =
		inject<PositionsOptionsService>(PositionsOptionsService)
	private readonly _alpacaAccountHelperService: AlpacaAccountHelperService =
		inject<AlpacaAccountHelperService>(AlpacaAccountHelperService)
	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _alertService: AlertService = inject<AlertService>(AlertService)

	protected alpacaAccount: WritableSignal<AlpacaAccountInterface | null> = signal<AlpacaAccountInterface | null>(null)
	protected isLoadingExecuteOption: WritableSignal<boolean> = signal<boolean>(false)

	protected totalSum: Signal<number> = computed<number>((): number => {
		const price: number = this.position()?.option_strike ?? 0
		const qtyAvailable: number = +(this.position()?.qty_available ?? 0)

		return price * this.optionSharesPerContractConstant * qtyAvailable
	})
	private _listenAlpacaAccount(): void {
		this._alpacaAccountHelperService.alpacaAccount$
			.pipe(takeUntil(this._destroy$))
			.subscribe((alpacaAccount: AlpacaAccountInterface | null): void => {
				this.alpacaAccount.set(alpacaAccount)
			})
	}

	protected onExecuteOption(): void {
		const position: PositionOptionInterface | null = this.position()
		if (!position) return

		this.isLoadingExecuteOption.set(true)
		this._positionsOptionsService
			.executePositionOption(position.symbol)
			.pipe(
				takeUntil(this._destroy$),
				finalize((): void => this.isLoadingExecuteOption.set(false)),
			)
			.subscribe(
				(): void => {
					this._alertService.success(this._translateService.instant('executeOption.optionExecuted'))
					this.onCloseDropdown()
				},
				(error: HttpErrorResponse): void => {
					this._alertService.error(
						error.error.error.message ??
							this._translateService.instant('executeOption.optionСouldNotBeExecuted'),
					)
				},
			)
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnInit(): void {
		this._listenAlpacaAccount()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
